import { useCallback, useRef } from 'react';
import { EcommerceImpression, EcommerceProductDetails, EcommercePurchase } from 'ts/shared/interfaces/Ecommerce';
import { useLocation } from 'react-router-dom';
import { Hotel } from 'ts/hotel/interfaces/HotelResponse';

const useDataLayer = () => {
  const ecommerceFlag = useRef(localStorage.getItem('__e_comerce'));
  const location = useLocation();
  const initDataLayer = useCallback(() => {
    if (typeof window !== 'undefined' && !window.dataLayer) {
      window.dataLayer = [];
    }
  }, []);

  const pushImpression = useCallback(
    (hotels: Hotel[]) => {
      if (!ecommerceFlag.current) {
        return;
      }
      const hotelImpressions = sessionStorage.getItem('hotelImpressions' + location.search);
      if (hotelImpressions) {
        return;
      }
      initDataLayer();
      try {
        if (typeof window !== 'undefined' && window.dataLayer) {
          const impressions = hotels.map((hotel, index) => ({
            id: hotel.item.code,
            name: hotel.item.name,
            category: 'HTL',
            price: Number(hotel.roomClasses[0].price.value.toFixed(2)),
            brand: 'HTL',
            variant: 'הלוך חזור',
            list: 'תוצאות חיפוש',
            position: index,
            dimension1: 0,
            dimension2: hotel.roomClasses[0].nights,
            dimension3: '',
          }));

          window.dataLayer.push({
            event: 'impression',
            ecommerce: {
              impressions,
            },
          } as EcommerceImpression);

          sessionStorage.setItem('hotelImpressions' + location.search, '1');
        }
      } catch (error) {
        console.error('Error pushing impression to dataLayer:', error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initDataLayer]
  );

  const pushHotelProductDetails = useCallback(
    (hotel: any) => {
      if (!ecommerceFlag.current) {
        return;
      }
      initDataLayer();
      try {
        if (typeof window !== 'undefined' && window.dataLayer) {
          window.dataLayer.push({
            event: 'productDetails',
            ecommerce: {
              id: hotel.item.code,
              name: hotel.item.name,
              category: 'HTL',
              price: Number(hotel.roomClasses[0].price.value.toFixed(2)),
              brand: 'HTL',
              variant: 'הלוך חזור',
              dimension1: 0,
              dimension2: hotel.roomClasses[0].nights,
              dimension3: '',
            },
          } as EcommerceProductDetails);
        }
      } catch (error) {
        console.error('Error pushing hotel product details to dataLayer:', error);
      }
    },
    [initDataLayer]
  );

  const pushHotelPurchase = useCallback(() => {
    if (!ecommerceFlag.current) {
      return;
    }
    initDataLayer();
    if (typeof window === 'undefined' || !window.dataLayer) {
      return;
    }

    try {
      const orderStr = sessionStorage.getItem('bookSuccessHotels');
      if (!orderStr) {
        console.warn('No bookSuccessHotels found in sessionStorage');
        return;
      }

      const order = JSON.parse(orderStr);
      const book = order?.book;
      const orderData = book?.order;

      if (!book || !orderData) {
        console.warn('Invalid order structure');
        return;
      }

      const orderId = order.orderId;
      const transactionId = order.transactionId;
      const trackingKey = `hotelPurchaseTracked_${orderId || transactionId}`;

      if (sessionStorage.getItem(trackingKey)) {
        return;
      }

      const hotelName = book.hotel?.item?.name || '';

      const numberOfNights = book.roomClass?.nights || 0;

      const hotelSegment = book.order.order.segments?.[0];
      const suppPnr = hotelSegment.suppPnr || '';
      const suppPnrSeg = hotelSegment.suppPnrSeg || '';
      const dimension3 = `${suppPnr} ${suppPnrSeg}`.trim();

      window.dataLayer.push({
        event: 'purchase',
        ecommerce: {
          purchase: {
            actionField: {
              id: transactionId,
              revenue: book.totalPrice + '',
            },
            products: [
              {
                id: book.hotel?.item?.code || '',
                name: hotelName,
                category: 'HTL',
                price: Number(book.totalPrice.toFixed(2)),
                brand: 'HTL',
                variant: 'הלוך חזור',
                dimension1: 0,
                dimension2: numberOfNights,
                dimension3: dimension3,
                quantity: 1,
                coupon: '',
              },
            ],
          },
        },
      } as EcommercePurchase);

      sessionStorage.setItem(trackingKey, 'true');
    } catch (error) {
      console.error('Error pushing hotel purchase to dataLayer:', error);
    }
  }, [initDataLayer]);

  return {
    pushImpression,
    pushHotelProductDetails,
    pushHotelPurchase,
  };
};

export default useDataLayer;
